import { ReactElement, useMemo, useState } from 'react';
import {
  ButtonContainer,
  ButtonOutsideContainer,
  CheckboxContainer,
  ExportInputsContainer,
} from './styles';
import { Select } from 'components/NewSelect';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'components';
import { Download1Icon } from 'icons';
import { colors } from 'styles/colors';
import { Option } from '../EnergyConsumptionGoal/types';
import { apiCallDownload } from 'providers';
import { useToast } from 'contexts/newToastContext';
import { useParams } from 'react-router-dom';
import { SelectSearchOption } from 'react-select-search';
import { NewSelectSearch } from '../EnergyConsumptionGoal/components/NewSelectSearch';

interface SpreadsheetExportProps {
  units: { id: number; name: string }[]
}

function generateYearOptions(): SelectSearchOption[] {
  const MINIMUM_YEAR = 2018;
  const INTERVAL = 10;
  const year = new Date().getFullYear();

  const options: SelectSearchOption[] = [];

  for (let currYear = MINIMUM_YEAR; currYear <= year + INTERVAL; currYear += 1) {
    options.push({
      name: currYear.toString(),
      value: currYear.toString(),
    });
  }

  return options;
}

export function SpreadsheetExport({
  units,
}: Readonly<SpreadsheetExportProps>): ReactElement {
  const currentYear = new Date().getFullYear();
  const [unit, setUnit] = useState<string[]>([]);
  const [year, setYear] = useState<string[]>([currentYear.toString()]);
  const { t } = useTranslation();
  const { toast } = useToast();
  const { clientId } = useParams<{ clientId: string }>();

  const unitOptions = units.map((item): SelectSearchOption => ({
    name: item.name,
    value: item.id.toString(),
  }));
  const yearOptions = generateYearOptions();
  const allUnitsSelected = useMemo(
    () => unitOptions.every((item) => unit.includes(item.value as string)),
    [unit, unitOptions],
  );

  function onSelectYear(value: string | string[]): void {
    setYear(value as string[]);
  }

  function onSelectUnit(value: string | string[]): void {
    setUnit(value as string[]);
  }

  function handleAllUnitsCheck(): void {
    if (allUnitsSelected) {
      setUnit([]);
      return;
    }
    setUnit(unitOptions.map((item) => item.value as string));
  }

  function generateDownloadFileLink(file: Blob, fileName: string): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(file);
    link.download = fileName;

    link.click();
    link.remove();
  }

  async function onClickExportButton(): Promise<void> {
    try {
      if (unit.length === 0) {
        toast(t('erroTitulo'), t('selecioneUnidade'), 'error');
        return;
      }

      const response = await apiCallDownload('/energy/export-goals-batch', {
        CLIENT_ID: Number(clientId),
        YEARS: year.map((item) => Number(item)),
        UNIT_IDS: unit.map((item) => Number(item)),
      });

      generateDownloadFileLink(
        response.data,
        response.headers.filename ?? 'Metas-de-Energia.xlsx',
      );

      toast(t('sucessoTitulo'), t('sucessoExportacao'), 'success');
    } catch (error) {
      console.error(error);
      toast(t('erroTitulo'), t('naoFoiPossivelExportar'), 'error');
    }
  }

  return (
    <ExportInputsContainer>
      <div>
        <NewSelectSearch
          label={t('unidade')}
          options={unitOptions}
          value={unit}
          onChange={onSelectUnit}
          closeOnSelect={false}
          multiple
        />
        <CheckboxContainer>
          <Checkbox
            size={24}
            checked={allUnitsSelected}
            onClick={handleAllUnitsCheck}
          />
          <span>{t('selecionarTodas')}</span>
        </CheckboxContainer>
      </div>
      <NewSelectSearch
        label={t('ano')}
        options={yearOptions}
        value={year}
        onChange={onSelectYear}
        closeOnSelect={false}
        multiple
        dataTestId="select-multiple-year"
      />
      <ButtonOutsideContainer>
        <Button
          type="button"
          variant="neutral-stroke-primary"
          style={{ width: '128px', height: '33px' }}
          onClick={onClickExportButton}
        >
          <ButtonContainer>
            <Download1Icon />
            <span>{t('baixarPlanilha')}</span>
          </ButtonContainer>
        </Button>
      </ButtonOutsideContainer>
    </ExportInputsContainer>
  );
}
